import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { ArrowLeft, Search, UserX, UserCheck, Trash2 } from 'lucide-react';
import { isAdminUser } from '../config/constants';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_ADMIN_SORT,
  ProfileSortKey,
  SortState,
  nextSortState,
  sortProfiles,
} from '../lib/adminTableSort';
import SortableTh from '../components/admin/SortableTh';
import { deleteUser } from '../lib/services/AdminService';

type Profile = {
  id: string;
  email: string;
  nom?: string;
  prenom?: string;
  created_at?: string;
  last_sign_in_at?: string;
};

const AdminImpersonate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { startImpersonation, stopImpersonation, impersonatedUserId } = useImpersonation();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState>(DEFAULT_ADMIN_SORT);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (!user || !isAdminUser(user.email)) {
      navigate('/');
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        setLoading(true);
        setError(null);

        let loaded: Profile[] | null = null;
        let lastError: unknown = null;

        if (isAdminUser(user?.email)) {
          try {
            const { data: rpcData, error: rpcError } = await supabase.rpc('admin_get_all_profiles');
            if (rpcError) {
              lastError = rpcError;
            } else {
              loaded = ((rpcData as any[]) || []).map((p) => ({
                id: p.id,
                email: p.email,
                nom: p.nom,
                prenom: p.prenom,
                created_at: p.created_at,
                last_sign_in_at: p.last_sign_in_at,
              }));
            }
          } catch (rpcEx) {
            lastError = rpcEx;
          }
        }

        if (!loaded) {
          const { data, error: tableError } = await supabase
            .from('profiles')
            .select('id, email, nom, prenom, created_at')
            .order('created_at', { ascending: false });
          if (tableError) throw lastError || tableError;
          loaded = data || [];
        }

        setProfiles(loaded || []);
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Erreur lors du chargement des utilisateurs';
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfiles();
  }, [user]);

  const filtered = useMemo(() => {
    const list = !search.trim()
      ? profiles
      : profiles.filter((p) => {
          const q = search.toLowerCase();
          return (
            p.email?.toLowerCase().includes(q) ||
            p.nom?.toLowerCase().includes(q) ||
            p.prenom?.toLowerCase().includes(q)
          );
        });
    return sortProfiles(list as Record<string, unknown>[], sort) as Profile[];
  }, [profiles, search, sort]);

  const handleSort = (key: ProfileSortKey) => {
    setSort((prev) => nextSortState(prev, key));
  };

  const handleDeleteUser = async (p: Profile) => {
    if (p.id === user?.id) {
      setToast({ type: 'error', message: 'Impossible de supprimer votre propre compte' });
      return;
    }

    const warn =
      `Supprimer définitivement ${p.email} ?\n\n` +
      `Compte + données SuiviFinance et autres apps (travaux, mariage, brocante…). Irréversible.`;
    if (!window.confirm(warn)) return;

    const confirmEmail = window.prompt(
      `Pour confirmer, saisissez l'email exact :\n${p.email}`,
    );
    if (confirmEmail?.trim().toLowerCase() !== p.email.trim().toLowerCase()) {
      setToast({ type: 'error', message: 'Suppression annulée (email non confirmé)' });
      return;
    }

    setDeletingUserId(p.id);
    setToast(null);
    try {
      const { data, error: delError } = await deleteUser(p.id);
      if (delError || !data?.success) {
        const message =
          (typeof delError === 'object' && delError && 'message' in delError
            ? String((delError as { message?: string }).message)
            : null) ||
          (typeof delError === 'string' ? delError : null) ||
          data?.error ||
          'Erreur lors de la suppression';
        setToast({ type: 'error', message });
        return;
      }
      const authDeleted = Number(data.deleted_counts?.auth_users ?? 0) > 0;
      if (!authDeleted) {
        setToast({
          type: 'error',
          message:
            data.error ||
            'Suppression incomplète : le compte auth est toujours présent. Réessayez.',
        });
        return;
      }
      setProfiles((prev) => prev.filter((x) => x.id !== p.id));
      setToast({
        type: 'success',
        message: data.message || `Utilisateur ${p.email} supprimé`,
      });
    } catch (e: unknown) {
      setToast({
        type: 'error',
        message: e instanceof Error ? e.message : 'Erreur lors de la suppression',
      });
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleImpersonate = (p: Profile) => {
    startImpersonation(p.id, p.email);
    navigate('/');
  };

  const handleExit = () => {
    stopImpersonation();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-dark-300 hover:text-white inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <h1 className="text-3xl font-bold text-white mt-2">Impersonation (Admin)</h1>
          <p className="text-dark-400">
            Se connecter temporairement en tant qu&apos;un utilisateur pour reproduire un bug signalé.
          </p>
        </div>
        {impersonatedUserId && (
          <button
            onClick={handleExit}
            className="inline-flex items-center gap-2 px-3 py-2 bg-amber-900/40 border border-amber-700 text-amber-200 rounded-md hover:bg-amber-800/50"
          >
            <UserX className="w-4 h-4" /> Quitter l&apos;impersonation
          </button>
        )}
      </div>

      {toast && (
        <div
          className={`px-4 py-3 rounded-lg border text-sm ${
            toast.type === 'success'
              ? 'bg-green-900/30 border-green-700 text-green-200'
              : 'bg-red-900/30 border-red-700 text-red-200'
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400 w-5 h-5" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par email, nom, prénom"
          className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <div className="bg-dark-960 border border-dark-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-dark-900 text-dark-300">
            <tr>
              <SortableTh
                label="Email"
                sortKey="email"
                activeKey={sort.key}
                dir={sort.dir}
                onSort={handleSort}
              />
              <SortableTh
                label="Nom"
                sortKey="nom"
                activeKey={sort.key}
                dir={sort.dir}
                onSort={handleSort}
              />
              <SortableTh
                label="Prénom"
                sortKey="prenom"
                activeKey={sort.key}
                dir={sort.dir}
                onSort={handleSort}
              />
              <SortableTh
                label="Créé le"
                sortKey="created_at"
                activeKey={sort.key}
                dir={sort.dir}
                onSort={handleSort}
              />
              <SortableTh
                label="Dernière connexion"
                sortKey="last_sign_in_at"
                activeKey={sort.key}
                dir={sort.dir}
                onSort={handleSort}
              />
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-dark-400">
                  Chargement…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-red-400">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-dark-400">
                  Aucun utilisateur
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              filtered.map((p) => (
                <tr key={p.id} className="border-t border-dark-800 hover:bg-dark-900/40">
                  <td className="px-4 py-3 text-white">{p.email}</td>
                  <td className="px-4 py-3 text-dark-200">{p.nom || '-'}</td>
                  <td className="px-4 py-3 text-dark-200">{p.prenom || '-'}</td>
                  <td className="px-4 py-3 text-dark-400">
                    {p.created_at ? new Date(p.created_at).toLocaleString('fr-FR') : '-'}
                  </td>
                  <td className="px-4 py-3 text-dark-400">
                    {p.last_sign_in_at ? new Date(p.last_sign_in_at).toLocaleString('fr-FR') : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => handleImpersonate(p)}
                        title="Se connecter en tant que cet utilisateur"
                        className="p-2 rounded-md bg-primary-600/20 hover:bg-primary-600/30 text-primary-300 hover:text-primary-200 transition-colors"
                        aria-label={`Impersonner ${p.email}`}
                      >
                        <UserCheck className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteUser(p)}
                        disabled={deletingUserId === p.id || p.id === user?.id}
                        title="Supprimer l’utilisateur et toutes ses données"
                        className="p-2 rounded-md text-red-400/80 hover:text-red-300 hover:bg-red-900/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        aria-label={`Supprimer ${p.email}`}
                      >
                        {deletingUserId === p.id ? (
                          <div className="w-5 h-5 border-2 border-red-300 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminImpersonate;
